'use client';

import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

/* ─── Animation timeline config ─── */
const TYPING_SPEED_MS = 40;

const DELAYS = {
  initialWait: 1500,
  afterCommand: 400,
  lineInterval: 120,
  betweenCommands: 1200,
  beforeReset: 2500,
} as const;

/* ─── Terminal line types ─── */
type TerminalLine =
  | { type: 'prompt'; command: string }
  | { type: 'output'; text: string; cls?: string }
  | { type: 'blank' };

/* ─── Command sequences ─── */
const SEQUENCES: TerminalLine[][] = [
  // 1. Convention sync
  [
    { type: 'prompt', command: 'agentteams sync' },
    { type: 'output', text: '✓ Synced 8 conventions', cls: 'text-success' },
    { type: 'output', text: '  context.md ............ up to date' },
    { type: 'output', text: '  conventions.md ........ updated' },
    { type: 'output', text: '  frontend.md ........... up to date' },
    { type: 'output', text: '  schema.md ............. new', cls: 'text-info' },
  ],
  // 2. Plan start
  [
    { type: 'prompt', command: 'agentteams plan start --id a3f2b1c8' },
    { type: 'output', text: '▶ Plan: Implement auth flow', cls: 'text-primary' },
    { type: 'output', text: '  Status: IN_PROGRESS' },
    { type: 'output', text: '  Steps: 3 tasks, 0 completed' },
    { type: 'output', text: '✓ Agent session started', cls: 'text-success' },
  ],
  // 3. Search
  [
    { type: 'prompt', command: 'agentteams search --query "auth"' },
    { type: 'output', text: '  Found 4 results:', cls: 'text-neutral-content/70' },
    { type: 'blank' },
    { type: 'output', text: '  Plan  Auth flow implementation', cls: 'text-primary' },
    { type: 'output', text: '  CR    Auth middleware refactor', cls: 'text-accent' },
    { type: 'output', text: '  Conv  auth-conventions.md', cls: 'text-info' },
    { type: 'output', text: '  PM    Auth token leak incident', cls: 'text-warning' },
  ],
  // 4. Plan finish
  [
    { type: 'prompt', command: 'agentteams plan finish --id a3f2b1c8' },
    { type: 'output', text: '✓ Quality score: 94', cls: 'text-success' },
    { type: 'output', text: '✓ Report uploaded', cls: 'text-success' },
    { type: 'output', text: '✓ Plan completed', cls: 'text-success' },
    { type: 'output', text: '  → View: agentteams.dev/r/a3f2b1c8', cls: 'text-primary' },
  ],
];

/* ─── Main component ─── */

export function AnimatedTerminalMockup({ className = '' }: { className?: string }) {
  const [lines, setLines] = useState<{ line: TerminalLine; key: number }[]>([]);
  const [typingText, setTypingText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const keyCounter = useRef(0);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (typingRef.current) {
      clearInterval(typingRef.current);
      typingRef.current = null;
    }
  }, []);

  const delay = useCallback(
    (ms: number): Promise<void> =>
      new Promise((resolve) => {
        timerRef.current = setTimeout(() => {
          if (mountedRef.current) resolve();
        }, ms);
      }),
    [],
  );

  const typeChars = useCallback(
    (text: string, updater: (partial: string) => void): Promise<void> =>
      new Promise((resolve) => {
        let idx = 0;
        typingRef.current = setInterval(() => {
          if (!mountedRef.current) {
            clearInterval(typingRef.current!);
            return;
          }
          idx++;
          updater(text.slice(0, idx));
          if (idx >= text.length) {
            clearInterval(typingRef.current!);
            typingRef.current = null;
            resolve();
          }
        }, TYPING_SPEED_MS);
      }),
    [],
  );

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    const run = async () => {
      while (!cancelled && mountedRef.current) {
        // Reset
        setLines([]);
        setTypingText('');
        setShowCursor(true);
        keyCounter.current = 0;
        await delay(DELAYS.initialWait);
        if (cancelled) break;

        for (const sequence of SEQUENCES) {
          if (cancelled) break;

          for (const line of sequence) {
            if (cancelled) break;

            if (line.type === 'prompt') {
              // Type the command character by character
              setTypingText('');
              await typeChars(line.command, (partial) => {
                setTypingText(partial);
              });
              if (cancelled) break;
              await delay(DELAYS.afterCommand);
              if (cancelled) break;

              // Commit typed command to lines
              const k = keyCounter.current++;
              setTypingText('');
              setLines((prev) => [...prev, { line, key: k }]);
            } else {
              // Output lines appear with delay
              await delay(DELAYS.lineInterval);
              if (cancelled) break;
              const k = keyCounter.current++;
              setLines((prev) => [...prev, { line, key: k }]);
            }
          }

          if (cancelled) break;
          await delay(DELAYS.betweenCommands);
        }

        if (cancelled) break;
        setShowCursor(false);
        await delay(DELAYS.beforeReset);
      }
    };

    void run();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearTimers();
    };
  }, [delay, typeChars, clearTimers]);

  return (
    <div
      className={`overflow-hidden rounded-lg border border-base-300 bg-neutral text-neutral-content shadow-inner ${className}`}
      // 이 목업은 winter(라이트) 테마 안에 놓인 어두운 표면이다. 밝은 배경 기준으로 잡힌
      // 시맨틱 색을 그대로 쓰면 대비가 무너진다 — 실측으로 text-success 3.22:1,
      // text-primary 3.54:1, text-accent 4.18:1로 모두 AA 미달이었다. 이 서브트리에서만
      // night 값으로 되돌린다. 클래스는 그대로 두고 토큰만 바꾸므로 새 줄을 추가해도 따라온다.
      style={
        {
          '--color-success': 'var(--color-success-on-dark)',
          '--color-primary': 'var(--color-primary-on-dark)',
          '--color-accent': 'var(--color-accent-on-dark)',
        } as CSSProperties
      }
    >
      <style>{`
        @keyframes terminal-line-in {
          from { opacity: 0; transform: translateY(2px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes terminal-cursor-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>

      {/* Title bar */}
      <div className="flex items-center gap-1.5 border-b border-neutral-content/10 px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-error/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-warning/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-success/60" />
        <span className="ml-2 text-[10px] text-neutral-content/70">agentteams-cli</span>
      </div>

      {/* Terminal body */}
      <div className="h-[260px] space-y-0.5 overflow-hidden px-3 py-3 font-mono text-[10px] leading-relaxed">
        {lines.map(({ line, key }) => (
          <div key={key} style={{ animation: 'terminal-line-in 0.2s ease-out both' }}>
            {line.type === 'prompt' ? (
              <p>
                <span className="text-success">$</span> <span className="text-neutral-content/70">{line.command}</span>
              </p>
            ) : line.type === 'blank' ? (
              <p>&nbsp;</p>
            ) : (
              <p className={line.cls ?? 'text-neutral-content/70'}>{line.text}</p>
            )}
          </div>
        ))}

        {/* Currently typing line */}
        {typingText.length > 0 && (
          <p>
            <span className="text-success">$</span> <span className="text-neutral-content/70">{typingText}</span>
            <span
              className="inline-block h-3 w-px bg-neutral-content/70"
              style={{ animation: 'terminal-cursor-blink 0.8s step-end infinite' }}
            />
          </p>
        )}

        {/* Idle cursor */}
        {typingText.length === 0 && showCursor && (
          <p>
            <span className="text-success">$</span>{' '}
            <span
              className="inline-block h-3 w-px bg-neutral-content/70"
              style={{ animation: 'terminal-cursor-blink 0.8s step-end infinite' }}
            />
          </p>
        )}
      </div>
    </div>
  );
}
